import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import {
  useMarkKadryContactedMutation,
  useSendKadryOfferMutation,
} from '@shared/api';
import {
  kadryDateTime,
  kadryErrorDetails,
  type KadryDelivery,
  type KadryOutreach,
  type KadryRegistryRow,
} from '@entities/kadry';
import { KadryErrorBanner } from '../../ui';
import { Side, Small, Channels } from './styles';
import {
  CHANNEL_TITLE,
  RESPONSE_CHANNEL,
  DELIVERY_TEXT,
  deliveryTone,
} from './types';

export function OutreachState({
  row,
  requestId,
  canSend,
  canMark,
}: {
  row: KadryRegistryRow;
  requestId: string;
  canSend: boolean;
  canMark: boolean;
}) {
  const [send, sendState] = useSendKadryOfferMutation();
  const [mark, markState] = useMarkKadryContactedMutation();
  const o: KadryOutreach | null = row.outreach;
  const reasons = sendState.error ? kadryErrorDetails(sendState.error).reasons : undefined;

  if (!o && row.applied) {
    return (
      <Side>
        <Small>Сам откликнулся на вакансию — предложение не нужно, свяжитесь по контактам.</Small>
      </Side>
    );
  }

  if (!o) {
    return (
      <Side>
        {canSend && (
          <Button
            type="button"
            $size="sm"
            disabled={sendState.isLoading}
            onClick={() => send({ matchId: row.match.id, requestId })}
          >
            {sendState.isLoading ? 'Отправляем…' : 'Отправить предложение'}
          </Button>
        )}
        {sendState.error ? (
          <>
            <KadryErrorBanner error={sendState.error} onDismiss={() => sendState.reset()} />
            {Array.isArray(reasons) && (
              <Small>
                {reasons.filter((r): r is string => typeof r === 'string').map((r) => (
                  <div key={r}>• {r}</div>
                ))}
              </Small>
            )}
          </>
        ) : null}
      </Side>
    );
  }

  const offers = o.deliveries.filter((d) => d.kind === 'offer');
  // Повторный вопрос ушёл хотя бы в один канал (иначе — только отметка срока).
  const followupAsked = o.deliveries.some(
    (d) => d.kind === 'followup' && d.status !== 'skipped' && d.status !== 'failed',
  );
  return (
    <Side>
      <Channels>
        {offers.map((d: KadryDelivery) => (
          <span key={d.channel} title={d.detail ?? undefined}>
            <Badge $tone={deliveryTone(d)}>
              {CHANNEL_TITLE[d.channel] ?? d.channel}: {DELIVERY_TEXT[d.status]}
            </Badge>
          </span>
        ))}
      </Channels>
      {o.auto && (
        <Small title="Балл модели не ниже порога автопредложения — отправлено без кадровика">
          Отправлено автоматически {kadryDateTime(o.created_at)}
        </Small>
      )}
      {o.response === 'interested' && <Badge $tone="success">Подходит</Badge>}
      {o.response === 'not_interested' && <Badge $tone="neutral">Не подходит</Badge>}
      {o.response == null && <Badge $tone="info">Ждём ответа</Badge>}
      {o.responded_at && (
        <Small>
          Ответ {kadryDateTime(o.responded_at)}
          {o.response_channel ? ` (${RESPONSE_CHANNEL[o.response_channel] ?? o.response_channel})` : ''}
        </Small>
      )}
      {o.response === 'interested' && (
        <>
          {o.hr_not_contacted ? (
            <Badge $tone="danger">С кандидатом не связались</Badge>
          ) : o.hr_contacted_at ? (
            <Badge $tone="success">Связались</Badge>
          ) : o.followup_sent_at && !followupAsked ? (
            <Small>
              Спросить «С вами связались?» не удалось: кандидат не открывал бота. Уточните сами.
            </Small>
          ) : o.followup_sent_at ? (
            <Small>Спросили «С вами связались?» {kadryDateTime(o.followup_sent_at)}</Small>
          ) : o.followup_due_at ? (
            <Small>Спросим «С вами связались?» {kadryDateTime(o.followup_due_at)}</Small>
          ) : null}
          {canMark && !o.hr_contacted_at && (
            <Button
              type="button"
              $size="sm"
              $variant="secondary"
              disabled={markState.isLoading}
              onClick={() => mark({ outreachId: o.id, requestId })}
            >
              Я связался
            </Button>
          )}
        </>
      )}
    </Side>
  );
}
